/* eslint-disable no-unused-vars */
// buildCombinedCriteria maps Offline filter value (1) to criteria:{status:1}, but the backend
// status field is the exact offline-stream count (0=Online, 1=1 offline stream, 2=2 offline, …).
// To include cameras with 2+ offline streams, transform status:1 → not:{status:0}.
// When both Online and Offline are selected, drop the status filter entirely.

// Helper for the `and` array branch: applies status transformation to a single group entry.
function transformAndGroup(group) {
  if (group.status === 1) {
     
    const { status: _s, ...rest } = group;
    return { ...rest, not: { status: 0 } };
  }

  if (!Array.isArray(group.or)) return group;

  const statusOrValues = group.or.map((c) => c.status).filter((v) => v !== undefined);

  if (statusOrValues.length === 0) return group;

   
  const { or: _or, ...rest } = group;

  if (statusOrValues.includes(0) && statusOrValues.some((v) => v > 0)) return rest;
  if (statusOrValues.every((v) => v > 0)) return { ...rest, not: { status: 0 } };

  return group;
}

export function transformStatusCriteria(criteria) {
  if (!criteria || typeof criteria !== 'object') return criteria;

  // Handle `and` array — transform any status entries inside it.
  // buildCombinedCriteria puts each filter group as a separate and entry, so status
  // can end up as { status: 1 } or { or: [{ status: 1 }, { status: 2 }] } within the array.
  if (Array.isArray(criteria.and)) {
     
    const { and: _and, ...rest } = criteria;
    return { ...rest, and: criteria.and.map(transformAndGroup) };
  }

  // Status values may appear at the top level (status:1) or inside an or array.
  // An or array built by text search contains field-specific entries (name, description, …)
  // with no status key — those must be preserved. Only or entries that contain status
  // are treated as status-based criteria.
  const statusOrValues = Array.isArray(criteria.or)
    ? criteria.or.map((c) => c.status).filter((v) => v !== undefined)
    : [];

  // Top-level status:1 (Offline only). The or array, if present, is a text-search or array
  // (no status entries) and must be kept as-is.
  if (criteria.status === 1 && statusOrValues.length === 0) {
     
    const { status: _s, ...rest } = criteria;
    return { ...rest, not: { status: 0 } };
  }

  // Status values live inside the or array (produced when multiple filter values are selected).
  if (statusOrValues.length > 0) {
    if (statusOrValues.includes(0) && statusOrValues.some((v) => v > 0)) {
      // Both Online and Offline selected: drop status constraints entirely (show all).
       
      const { or: _or, ...rest } = criteria;
      return rest;
    }
    if (statusOrValues.every((v) => v > 0)) {
      // Only Offline values in or: replace with not:{status:0}.
       
      const { or: _or, ...rest } = criteria;
      return { ...rest, not: { status: 0 } };
    }
  }

  return criteria;
}

/**
 * Transforms jurisdiction filter criteria into the metadata_filter shape
 * the backend expects.
 *
 * buildCombinedCriteria produces:
 *   single:   { jurisdiction: 'Abilene' }
 *   multiple: { or: [{ jurisdiction: 'Abilene' }, { jurisdiction: 'Paris' }] }
 *
 * This transformer converts those into:
 *   single:   { metadata_filter: { key: 'jurisdiction', value: 'Abilene' } }
 *   multiple: { or: [
 *                { metadata_filter: { key: 'jurisdiction', value: 'Abilene' } },
 *                { metadata_filter: { key: 'jurisdiction', value: 'Paris' } }
 *              ]}
 */
// Maps frontend field names to their backend metadata key names
const METADATA_FIELD_MAP = {
  jurisdiction: 'jurisdiction',
  routeName: 'route name',
  trafficDirection: 'traffic direction',
  mileMarker: 'mile marker',
};

const toMetadataFilter = (key, value) => ({
  metadata_filter: { key: `regex:(?i)^${key}$`, value },
});

const transformMetadataGroup = (group) => {
  let result = { ...group };
  Object.entries(METADATA_FIELD_MAP).forEach(([field, metaKey]) => {
    if (field in result) {
      const value = result[field];
      delete result[field];
      result = { ...result, ...toMetadataFilter(metaKey, value) };
    }
  });
  // Handle or array inside the group
  if (Array.isArray(result.or)) {
    const metadataEntries = result.or.filter((c) =>
      Object.keys(METADATA_FIELD_MAP).some((field) => field in c)
    );
    const otherEntries = result.or.filter((c) =>
      !Object.keys(METADATA_FIELD_MAP).some((field) => field in c)
    );
    if (metadataEntries.length > 0) {
      const converted = metadataEntries.map((c) => {
        const field = Object.keys(METADATA_FIELD_MAP).find((f) => f in c);
        return toMetadataFilter(METADATA_FIELD_MAP[field], c[field]);
      });
      const newOr = [...converted, ...otherEntries];
      const { or: _or, ...rest } = result;
      return newOr.length === 1 ? { ...rest, ...newOr[0] } : { ...rest, or: newOr };
    }
  }
  return result;
};

export function transformMetadataCriteria(criteria) {
  if (!criteria || typeof criteria !== 'object') return criteria;

  // Handle `and` array
  if (Array.isArray(criteria.and)) {
    const { and: _and, ...rest } = criteria;
    return { ...rest, and: criteria.and.map(transformMetadataGroup) };
  }

  // Handle top-level or array
  if (Array.isArray(criteria.or)) {
    const metadataEntries = criteria.or.filter((c) =>
      Object.keys(METADATA_FIELD_MAP).some((field) => field in c)
    );
    const otherEntries = criteria.or.filter((c) =>
      !Object.keys(METADATA_FIELD_MAP).some((field) => field in c)
    );
    if (metadataEntries.length > 0) {
      const converted = metadataEntries.map((c) => {
        const field = Object.keys(METADATA_FIELD_MAP).find((f) => f in c);
        return toMetadataFilter(METADATA_FIELD_MAP[field], c[field]);
      });
      const newOr = [...converted, ...otherEntries];
      const { or: _or, ...rest } = criteria;
      return { ...rest, or: newOr };
    }
  }

  // Handle top-level metadata fields
  let result = { ...criteria };
  Object.entries(METADATA_FIELD_MAP).forEach(([field, metaKey]) => {
    if (field in result) {
      const value = result[field];
      delete result[field];
      result = { ...result, ...toMetadataFilter(metaKey, value) };
    }
  });

  return result;
}
